// P2-4B — THE READING LABEL TABLE, MOVED TO A SHARED LAW (R37: move the data).
//
// **IT MOVED BECAUSE A NOTIFICATION LEAKED HER LIFE ONTO A LOCK SCREEN.** The generation push was
// composed from the SAVED TITLE, and a saved title is her words: her live case went out reading
// "Lashonda Jeanett Benning · What is my purpose? is ready" — her full legal name and the question
// she asked, on a screen anyone standing near her can read. Her ruling: canned per kind, naming
// exactly WHAT is ready and nothing else.
//
// That canned sentence needs the type roster on the SERVER, and this table lived in
// src/lib/labels.ts, which CLAUDE.md defines as client-only TypeScript. So the data moved here and
// labels.ts re-exports it: one declaration, both sides, and R32's fixtures keep reading it through
// labels.ts exactly as before.
//
// **THE FALLBACK IS THE LOAD-BEARING PART (R32).** `reportTypeLabel` ends in "Birth Chart", which is
// also a real type's correct answer — that collision is how `note`, `palm-reading` and
// `bubble-reading` read as "Birth Chart" on her shelf. For a NOTIFICATION the same fallback would be
// a lie on her phone, so `notificationLabel` below refuses to guess instead.
export const REPORT_TYPE_LABELS = {
  // A birth chart is DECLARED, never left to the fallback it happens to share (R23: named, not implied).
  "birth-chart": "Birth Chart",
  "numerology": "Numerology",
  "personality": "Personality & Purpose",
  "compatibility": "Compatibility Profile",
  "relationship": "Relationship Reading",
  "find-your-type": "Find Your Type",
  "tarot": "Tarot Reading",
  "tarot-spread": "Tarot Reading",
  "dream": "Dream Reading",
  "human-design": "Human Design",
  "human-design-compat": "Human Design Compatibility",
  "mini-report": "Mini Report",
  "astrology-tradition": "Astrology Reading",
  "prediction": "Prediction",
  "astrology-report": "Astrology Reading",
  "astrocartography-map": "Astrocartography Map",
  "astrocartography-location": "Astrocartography",
  "astrocartography-relocation": "Relocation Reading",
  "question-reading": "Your Question",
  // AKASHIC-1: the five soul-record readings. These are HER names for them, and they are the
  // roster's own — `scripts/akashic-law.mjs` holds them character-for-character against
  // `src/akashic.js`, so a shelf row can never drift from the card that offered the reading.
  "akashic-soul-record": "Your Soul Record",
  "akashic-past-life-connections": "Past-Life Connections",
  "akashic-soul-contracts": "Your Soul Contracts",
  "akashic-past-life-gifts": "Past-Life Gifts and Talents",
  "akashic-purpose-timeline": "Your Soul's Purpose Timeline",
  // R32: the three that were reading as "Birth Chart" on her own shelf.
  "palm-reading": "Palm Reading",
  "bubble-reading": "Your Reading",
  "note": "A Note",
  // L11 — the reading a stranger was given before she was a member. **IT IS NAMED IN HER WORDS AND
  // NOT IN THE HOUSE'S MACHINERY**: this value is what a member reads on her own shelf, and `funnel`
  // is a word about advertising rather than about her (S1-A/R117 — a member-facing line never names
  // the machinery). One label for all three doors, because the shelf is keyed on the TYPE and the
  // three funnels are one artefact wearing three questions.
  "funnel-reading": "The Reading That Brought You Here",
  // The Magic Room, worded exactly as `magicHeading` heads the document itself.
  "chant": "A Chant",
  "affirmation": "An Affirmation",
  "incantation": "An Incantation",
  "spell": "A Spell",
  "spell-working": "A Spell",
  "oils-working": "A Spelled Oil",
  "oil-working": "A Spelled Oil",
  "oil": "A Spelled Oil",
  "herb-working": "An Herb",
  "herb-report": "An Herb",
  "herb": "An Herb",
  "keys-working": "A Working from the Greater Keys",
  "keys": "A Working from the Greater Keys",
  "moses-working": "A Working from the Books of Moses",
  "moses": "A Working from the Books of Moses",
  "cauldron-working": "A Working from the Cauldron",
  "cauldron": "A Working from the Cauldron",
  "petition": "A Petition",
  "seal": "A Seal",
  "seals": "A Seal",
};

export function reportTypeLabel(type) {
  return REPORT_TYPE_LABELS[String(type || "")] || "Birth Chart";
}

// P2-4B — WHAT A PUSH IS ALLOWED TO SAY A READING IS.
//
// Unlike `reportTypeLabel` this NEVER falls back to a plausible name. A type nobody rostered gets
// the honest generic ("Your reading"), and the fixture fails on any type the server can write that
// is missing from the table — so a new generator cannot ship a push that names the wrong reading,
// and cannot ship one that leaks instead.
export function notificationLabel(type) {
  const key = String(type || "");
  return Object.prototype.hasOwnProperty.call(REPORT_TYPE_LABELS, key) ? REPORT_TYPE_LABELS[key] : "";
}

// Her spec, verbatim in shape: "Your Birth Chart is ready." / "Your seal is ready."
export function readingReadySentence(type) {
  const label = notificationLabel(type);
  if (!label) return "Your reading is ready.";
  // The table words several entries as a phrase ("A Chant", "A Petition", "Your Reading"), which
  // would read "Your A Chant is ready." The leading article is dropped so the sentence stays English.
  const noun = label.replace(/^(An|A|Your)\s+/i, "");
  return `Your ${noun} is ready.`;
}
